import React, { forwardRef, useCallback, useContext, useImperativeHandle } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { strings } from "../../services/Localization";
import PageContent from "../PageContent";
import { ActionType, BulkActionType, ColumnType, OptionType } from '../../components/Common/DataTable/DataTypes';
import { DEFAULT_INPUT_VALIDATION, DEFAULT_RECORD_LIMIT } from "../../components/Common/DataTable/DataTableConsts";
import { PageButtonType } from '../../datatypes/datatypes';
import _ from 'lodash';
import { getAppBase } from "../../utils/consts";
import { DataTableContext, deviceData } from "../../components/Common/DataTable/DataTableState";
import { toast } from "./../../utils/Toaster";
import { dateTimeString, deviceStatusSymbol, deviceStatusTooltip } from '../../utils/filters';
import { isMobile } from "react-device-detect";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBatteryEmpty, faBatteryExclamation, faBatteryFull, faBatteryHalf, faBatteryQuarter, faBatteryThreeQuarters, faPlug } from "@fortawesome/pro-regular-svg-icons";
import RenderTagsOverlay from "../../components/Common/RenderTagsOverlay";
import { IDevice } from "src/dassTypes";
import { GenericDassQuery } from "../../services/BasicDassQueries";
import { generateDateTimeColumn, generateDevEUIcolumn } from "../../components/Common/columnDefination";
import { TAB_DEVICE_DETAIL } from '../../datatypes/tabsconstants';


type FilterOptionType = {
    label: string;
    value: string | number;
}

interface IDeviceDetails extends IDevice {
    traffic_status?: "NOMINAL" | "NO_TRAFFIC" | "RATE_TOO_LOW" | "RATE_TOO_HIGH" | "NA";
    traffic_rate_ul_per_day?: number;
    fw_version?: string | { hex?: string; sem?: string };
}

const SelectDevices: React.FC<any> = forwardRef((props,ref) => {
    const navigate = useNavigate();
    let { campaignid, tabname } = useParams();
    const stateInit = {
        loggedUser: null,
        editCampaignId: "",
        addCampaign: false,
        pageTitle: strings.MY_APPLICATIONS_TITLE,     // should be translated
        showAlertModal: false,
        breadCrumbArr: [{ label: 'Select Devices', url: '' }],
        campaignid: '',
        selectedAction: '',
        showLoadingDialog: false,
        showConfirmDialog: false,
        confirmDialogeSettings: {
            title: '',
            description: '',
            actionLabel: '',
            confirmAction: () => { }
        },
        refresh: false,
        exportInit: 0,
    };

    const { state } = useLocation();

    useImperativeHandle(ref, () => ({
        goToDetails(){

            if( DataTableContextObj.searchState.selectedRowIds && DataTableContextObj.searchState.selectedRowIds.length > 0) {
                
                navigate(`${getAppBase()}/my-campaigns/create`, { state: { selectedDevices : DataTableContextObj.searchState.selectedRowIds,selectedFirmware: state['selectedFirmware'], row: {}, prevPageUrl: `${getAppBase()}/my-campaigns` } })
                
                return true;

            } else {
                toast.warning("Select devices first.");
                return false;
            }    
        }    
    }));

    const deviceProfileFetcher = async () => {
        try {
            if (!deviceData["deviceProfiles"]) {
                const response = await GenericDassQuery("/rest/device-profiles?show_region=true", { method: "GET" });
                deviceData["deviceProfiles"] = response.data.map((profile) => ({
                    label: profile.profile_name,
                    value: profile.profile_uuid,
                }));
            }
            return deviceData["deviceProfiles"];
        } catch (e) {
            console.log("Error fetching device profiles:", e);
            return [];
        }
    };

    const getActions = useCallback(() => {
        let actions: ActionType[] = [];
        let bulkActions: BulkActionType[] = [];

        return { actions, bulkActions}
    }, []);

    const detailPageNav = ( navigate, tab, deveui, row) => {
        navigate(`${getAppBase()}/my-devices/${deveui}/${tab}`, {state: {row, prevPageUrl: `${getAppBase()}/my-campaigns/create`}})
    }

    const renderDeviceProfile = (id) => {
        try {
            const profiles = deviceData["deviceProfiles"] || [];
            const profile = profiles.find((p) => p.value === id);
            return profile?.label || "";
        } catch (e) {
            console.log("Error rendering device profile:", e);
            return "";
        }
    };

    const renderBatteryIcon = (level: number | null | undefined) => {
        if (level === null || level === undefined || level === 255) return null;
        if (level === 0) return faPlug;

        const percent = Math.round(((level - 1) / 253) * 100);

        if (percent > 85) return faBatteryFull;
        if (percent > 60) return faBatteryThreeQuarters;
        if (percent > 40) return faBatteryHalf;
        if (percent > 15) return faBatteryQuarter;
        if (percent > 3)  return faBatteryEmpty;

        return faBatteryExclamation;
    };

    const renderBatteryTooltip = (row: IDeviceDetails): string => {
        const level = row.battery_level;
        const updateTimeText = row.battery_level_update_time
            ? `\nOn&#160;${dateTimeString(row.battery_level_update_time)}`
            : "";

        if (level === null || level === undefined) {
            return "No status available";
        }

        if (level === 255) {
            return `Battery status not supported.${updateTimeText}`;
        }

        if (level === 0) {
            return `Device is attached to power source.${updateTimeText}`;
        }

        const percent = Math.round(((level - 1) / 253) * 100);
        return `Battery level at ${percent}%.${updateTimeText}`;
    };

    let DataTableContextObj = useContext(DataTableContext);

    const initDataTable = () => {

        const optionFetcher = async (): Promise<FilterOptionType[]> => {
            const response = await GenericDassQuery("/rest/groups", { method: "GET" });
            const groups = response.data ?? [];
            return groups.map((g: { groupid: string }) => ({ label: g.groupid, value: g.groupid }));
        };

        const trafficStatusColorMap: Record<string, { label: string; color: string }> = {
            DEACTIVATED:  { label: "Deactivated",    color: "#C0392B" }, 
            SUSPENDED:    { label: "Suspended",      color: "#9A0000" },
            NOMINAL:      { label: "Normal Rate",    color: "#27AE60" }, 
            NO_TRAFFIC:   { label: "No Data 24h+",   color: "#aa0000" }, 
            RATE_TOO_LOW: { label: "Low Rate",       color: "#F39C12" }, 
            RATE_TOO_HIGH:{ label: "High Rate",      color: "#840d0d" }, 
            NA:           { label: "Never Sent",     color: "#A93226" }, 
        };

        const statusfilterOptions: FilterOptionType[] = [
            { label: "Never Seen",       value: "never_seen"          },
            { label: "MIC Error",        value: "seen_with_mic_error" },
            { label: "Joined",           value: "joined"              },
            { label: "Data Received",    value: "data_received"       },
            { label: "Deactivated",      value: "deactivated"         },
            { label: "Suspended",        value: "suspended"           },
        ];

        const trafficStatusOptions: FilterOptionType[] = [
            { value: "NOMINAL", label: "Normal Rate" },
            { value: "NO_TRAFFIC", label: "No Data 24h+" },
            { value: "RATE_TOO_LOW", label: "Low Rate" },
            { value: "RATE_TOO_HIGH", label: "High Rate" },
            { value: "NA", label: "Never Sent" },
        ];

        const { actions } = getActions();
        const appBase = getAppBase();
        const pageButtons: PageButtonType[] = [];

        let columns: ColumnType[] = [
            {
                key: "bulk_action_checkbox",
                type: "bulk_action_checkbox",
                title: "Bulk Action",
                filterable: false,
                cellWidth: 3,
                customClass: "sticky left-first",
            },
            generateDevEUIcolumn({
                key: "deveui",
                detailPageNav: (row: IDeviceDetails) => detailPageNav(navigate, TAB_DEVICE_DETAIL, row.deveui, row),
            }),
            {
                key: "image_url",
                title: strings.TABLE_IMAGE,
                type: "icon_with_tooltip",
                filterable: false,
                render: (row: IDeviceDetails) => {
                    const raw = row.image_url;
                    if (!raw) return null;
                    const imageUrl = raw.startsWith("storageid:") ? `/storagecache/${raw.substring(10)}` : raw;
                    return (
                        <img
                            src={imageUrl}
                            alt="Device"
                            style={{ maxHeight: 30, maxWidth: 80, height: "auto", width: "auto" }}
                        />
                    );
                },
                cellWidth: 20,
                dataAlign: "center",
                newCellWidth: isMobile ? "50px" : "80px",
                width: 80,
                minWidth: 80,
                maxWidth: 80,
            },
            {
                key: "device_status",
                title: strings.STATUS,
                type: "icon_with_tooltip",
                render_icon: (row: IDeviceDetails) => deviceStatusSymbol(row),
                render_tooltip: (row: IDeviceDetails) => deviceStatusTooltip(row),
                filterable: true,
                filterField: "search_device_status",
                filterType: "select",
                filterParams: {
                    mapper: (x) => x || undefined,
                    options: statusfilterOptions,
                },
                dataAlign:'center',
                cellWidth: 6,
                newCellWidth: "100px",
                width: 100,
                minWidth: 100,
                maxWidth: 100,
            },
            {
                key: "battery_level",
                dataAlign: "center",
                title: <FontAwesomeIcon icon={faBatteryHalf} />,
                header_tooltip: "Battery Level",
                type: "icon_with_tooltip",
                render_tooltip: (row: IDeviceDetails) => renderBatteryTooltip(row),
                render_icon: (row: IDeviceDetails) => renderBatteryIcon(row.battery_level),
                newCellWidth: "30px",
                width: 80,
                minWidth: 80,
                maxWidth: 80,
            },
            {
                key: "comment",
                title: strings.COMMENT,
                type: "text",
                filterable: true,
                filterField: "search_comment",
                filterType: "text",
                inputValidation: DEFAULT_INPUT_VALIDATION,
                sortable: true,
                sortKey: "sort_by_comment",
                filterParams: {},
                newCellWidth: '200px',
                extraClass: 'ow-datatable-overflow-ellipsis',
                dataAlign:'left',
                render_tooltip: (row: IDeviceDetails) => row.comment,
                width: 100,
                minWidth: 100,
                flex: 2,
                resizable: true
            },
            {
                key: "fw_version",
                title: "FW Version",
                type: "text",
                filterable: false,
                sortable: false,
                dataAlign: "left",
                newCellWidth: "120px",
                width: 120,
                minWidth: 100,
                flex: 1,
                render: (row: IDeviceDetails) => {
                    const fw = row.fw_version;
                    if (!fw) return "";
                    if (typeof fw === "string") return fw; // plain hex or sem
                    if (fw.hex && fw.sem) return `${fw.hex} (${fw.sem})`;
                    if (fw.hex) return fw.hex;
                    if (fw.sem) return fw.sem;
                    return "";
                },
                render_tooltip: (row: IDeviceDetails) => {
                    const fw = row.fw_version;
                    if (!fw) return "";
                    if (typeof fw === "string") return fw;
                    if (fw.hex && fw.sem) return `${fw.hex} (${fw.sem})`;
                    if (fw.hex) return fw.hex;
                    if (fw.sem) return fw.sem;
                    return "";
                },
                maxWidth: 400,
                resizable: true,
            },
            {
                key: "device_profile_uuid",
                title: 'Device Profile',
                type: "text",
                extraClass: 'ow-datatable-overflow-ellipsis',
                render: (row: IDeviceDetails) => renderDeviceProfile(row.device_profile_uuid),
                filterField: 'search_device_profile_uuid',
                filterable: true,
                filterType: "multiselect",
                newCellWidth: '200px',
                filterParams: {
                    optionFetcher: deviceProfileFetcher,
                    data: [],
                    mapper: x => x,
                },
                render_tooltip: (row) => renderDeviceProfile(row.device_profile_uuid),
                width: 100,
                minWidth: 100,
                flex: 2,
                resizable: true,
                showCopyLink: true,
            },
            {
                key: "groups",
                title: strings.MY_DEVICES_GROUPS,
                type: "text_with_tooltip",
                cellWidth: 25,
                cellWidthType: '%',
                dataAlign: "left",
                render: x => { return RenderTagsOverlay(x.groups, 'blue', `${appBase}/groups/~tag~/group-info`, {prevPageUrl:`${getAppBase()}/my-devices`,row: { groupid:0 },}, "Groups", "95px") },
                customNavigation: (x) => {
                    if(x.groups){return null} else {
                    detailPageNav(navigate, "device-detail", x.deveui, x )
                }},
                filterable: true,
                filterField: "group",
                filterType: "multiselect",                
                newCellWidth: "130px",
                extraClass: 'd-flex align-items-center', 
                filterParams: {
                    optionFetcher: optionFetcher,
                    data: [],
                    mapper: x => x,
                },
                width: 120,
                minWidth: 120,
                resizable: true,
            },
            {
                key: "traffic_status",
                title: strings.TRAFFIC_STATUS,
                type: "text",
                filterable: false,
                filterField: "search_traffic_status",
                filterType: "select",
                filterParams: {
                    mapper: (x) => x || undefined,
                    options: trafficStatusOptions,
                },
                sortable: false,
                sortKey: "sort_by_traffic_status",
                newCellWidth: "130px",
                dataAlign: "left",
                render: (row: IDeviceDetails) => {
                    if (!row?.last_reception) {
                        const neverSent = trafficStatusOptions.find(opt => opt.value === "NA");
                        return neverSent ? neverSent.label : "Never Sent";
                    }
                    if (row.suspended) {
                        const { label, color } = trafficStatusColorMap["SUSPENDED"];
                        return <span style={{ color }}>{label}</span>;
                    }
                    if (!row?.last_reception) {
                        const {label, color} = trafficStatusColorMap["NA"];
                        return <span style={{ color }}>{label}</span>;
                    }
                    if (status && trafficStatusColorMap[status]) {
                        const { label, color } = trafficStatusColorMap[status];
                        return <span style={{ color }}>{label}</span>;
                    }
                    return <span>{status ?? ""}</span>;
                },
                cellWidth: 10,
                width: 100,
                minWidth: 100,
                flex: 2,
                resizable: true
            },
            generateDateTimeColumn({
                key: "last_reception",
                title: "Last Uplink",
                filterable: true,
                filterField: "search_last_reception",
                sortable: true,
                sortKey: "sort_by_date",
            })
        ];

        let options: OptionType = {
            url: '/uiapi/rest/nodes',
            query_param: { all: true, get_pages: true, limit: DEFAULT_RECORD_LIMIT, stream: 'progress' },
            serial_number: false,
            id_field: 'deveui',
            oboe_path: 'pages.*',
            available_key: 'deveui',
            allowBulkActions: true,
            defaultSortField: 'sort_by_deveui',
            defaultSortOrder: 'asc',
            emptyDataMsg: strings.NO_DEVICE_AVAILABLE,
            modal: false,
            hideBulkActions: true,
            agGridAdjustWidthKeys: ['comment'],
            disableSelectAllOption : true,
        }

        return {
            ...actions,
            columns,
            ...options,
            pageButtons,
        };
    }

    let refresh = stateInit.refresh;
    
    return (
        <>
            <div className="row fuota-stepper-device">
                <div className="col">
                    <PageContent
                        name="my-select-devices"
                        id={campaignid}
                        tabname={tabname}
                        actions={getActions()}
                        breadCrumbArr={[]}
                        pageButtons={[]}
                        countLabel={`Select Devices`}
                        dataTableOption={initDataTable()}
                        exportInit={stateInit.exportInit}
                        refresh={refresh}
                        agGridTableHeight="calc(100% - 85px)"
                        stepperIndex = {1}
                        >

                    </PageContent>
                </div>
            </div>
        </>
    )
})

export default SelectDevices;